import {
    LogMessageType,
    RecipientType,
    GeException,
    NotificationDialog,
    GlobalInfrastructureServices,
    Container,
    getUrlParameter,
    Scopes
} from '../infrastructure';
import {
    AllRecipientTypes,
    AuditAttachment,
    RecipientForDialog,
    RecipientsByAllTypes,
    SensitivityLabel,
    ValidateEmailAddressesResponse,
    AuditEmailRecipient,
    ExtraServerAuditData,
    TokenData,
    Identity,
    NativeLinksToUpdate,
    ValidateResponseRecipient,
    ValidationResponse
} from './types';
import { AuditEvents, constructAuditEventData, decodeHtmlEntities, EmailFields, formatToUuid, withTimeout, withTimeoutOr, TimeoutError } from '../infrastructure/utils';
import { GePortals } from './GePortals';
import axios, { AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { DECISION_ACCEPT, DECISION_OVERRIDE, ignoreChecksLabelsId, SERVER_URL, BYPASS_KEYWORD, OFFICE_JS_CALL_TIMEOUT_MS, SAVE_DRAFT_TIMEOUT_MS, NOTIFICATION_TIMEOUT_MS, HTTP_REQUEST_TIMEOUT_MS } from '../infrastructure/constants';
import { ApmBase, Transaction } from '@elastic/apm-rum';
import { AuditEventQueue } from '../../auditQueue/AuditEventQueue';
import { jwtDecode } from "jwt-decode";

export interface GlobalServiceInterface extends GlobalInfrastructureServices {
    geApi: GeApi,
    apm: ApmBase
};

export type GeApiState = {
    outlookToken: string,
    emailId: string,
    itemType: Office.MailboxEnums.ItemType,
    auditTraceId: string,
    decodedTokenPayload: any,
    label: SensitivityLabel,
    attachments: AuditAttachment[],
    allRecipients: AuditEmailRecipient[],
    nativeLinks: string[],
    nativeLinksToUpdate: NativeLinksToUpdate[],
    setLabelFromOutlook: boolean,
    extraServerAuditData: ExtraServerAuditData,
    subject: string,
    recipientsToBeRemoved: AuditEmailRecipient[],
    recipientsOverriddenByUser: RecipientForDialog[],
    serverUrl: string,
    tenant: string,
    originalRecipientsByType: RecipientsByAllTypes
    cwpToken: TokenData
};

const UserActions = {
    SEND: 'send',
    CANCEL: 'cancel',
    FORCE_SEND: 'force_send'
}

const headerName = 'eshareemailgovernance';
const headerValue = 'true';

export class GeApi {

    protected globalService: GlobalServiceInterface;
    protected container: Container;
    protected outlookToken: string;
    protected identity: Identity;
    protected emailId: string;
    protected itemType: Office.MailboxEnums.ItemType;
    protected auditTraceId: string;
    protected decodedTokenPayload: any;
    public label: SensitivityLabel;
    protected attachments: AuditAttachment[];
    protected allRecipients: AuditEmailRecipient[];
    protected nativeLinks: string[];
    protected nativeLinksToUpdate: NativeLinksToUpdate[];
    protected setLabelFromOutlook: boolean;
    protected extraServerAuditData: ExtraServerAuditData;
    public subject: string;
    protected apmTransaction: Transaction;
    public cwpToken: TokenData;
    public recipientsToBeRemoved: AuditEmailRecipient[] = [];
    public recipientsOverriddenByUser: RecipientForDialog[] = [];
    public serverUrl: string;
    public tenant: string;
    protected originalRecipientsByType: RecipientsByAllTypes;
    public auditsAttempted: any[] = [];

    /**
     * This function is called upon the creation of the object (constructor).
     * @param logger the global logger instance
     */
    constructor(globalService: GlobalServiceInterface, container: Container, itemType: Office.MailboxEnums.ItemType) {
        this.globalService = globalService;
        this.container = container;
        this.itemType = itemType;
        this.auditTraceId = uuidv4();
        this.allRecipients, this.recipientsToBeRemoved = [];
        this.setLabelFromOutlook = false;
        this.serverUrl = getUrlParameter('server_url');
        this.tenant = getUrlParameter('deployment');

        AuditEventQueue.setApm(this.globalService.apm);
    }

    public getState(): GeApiState {
        return {
            outlookToken: this.outlookToken,
            emailId: this.emailId,
            itemType: this.itemType,
            auditTraceId: this.auditTraceId,
            decodedTokenPayload: this.decodedTokenPayload,
            label: this.label,
            attachments: this.attachments,
            allRecipients: this.allRecipients,
            nativeLinks: this.nativeLinks,
            nativeLinksToUpdate: this.nativeLinksToUpdate,
            setLabelFromOutlook: this.setLabelFromOutlook,
            extraServerAuditData: this.extraServerAuditData,
            subject: this.subject,
            recipientsToBeRemoved: this.recipientsToBeRemoved,
            recipientsOverriddenByUser: this.recipientsOverriddenByUser,
            serverUrl: this.serverUrl,
            tenant: this.tenant,
            originalRecipientsByType: this.originalRecipientsByType,
            cwpToken: this.cwpToken
        };
    }

    public setState(state: GeApiState): void {
        this.outlookToken = state.outlookToken;
        this.emailId = state.emailId;
        this.itemType = state.itemType;
        this.auditTraceId = state.auditTraceId;
        this.decodedTokenPayload = state.decodedTokenPayload;
        this.label = state.label;
        this.attachments = state.attachments;
        this.allRecipients = state.allRecipients;
        this.nativeLinks = state.nativeLinks;
        this.setLabelFromOutlook = state.setLabelFromOutlook;
        this.extraServerAuditData = state.extraServerAuditData;
        this.subject = state.subject;
        this.recipientsToBeRemoved = state.recipientsToBeRemoved;
        this.recipientsOverriddenByUser = state.recipientsOverriddenByUser || [];
        this.serverUrl = state.serverUrl;
        this.tenant = state.tenant;
        this.originalRecipientsByType = state.originalRecipientsByType;
        this.cwpToken = state.cwpToken;
    }

    public getAllRecipients() {
        return this.allRecipients;
    }

    public getOutlookToken() {
        return this.outlookToken;
    }

    public getEmailId() {
        return this.emailId;
    }

    public getLabel() {
        return this.label;
    }

    public getAttachments() {
        return this.attachments;
    }

    public getNativeLinks() {
        return this.nativeLinks;
    }

    private async setAllRecipients(recipientsByTypeToSet: RecipientsByAllTypes): Promise<void> {
        for (const [key, value] of Object.entries(recipientsByTypeToSet)) {
            // This writes the corrected recipient list back after validation, so a timeout here means
            // the mail may go out with recipients policy intended to remove. We still fail open, in
            // line with every other failure on this path, but the timeout is recorded by
            // withTimeoutOr as `timed-out:<operation>` so the case is auditable rather than silent.
            await withTimeoutOr(
                this.globalService.outlookActions.setRecipients(
                    key as AllRecipientTypes,
                    value
                ),
                OFFICE_JS_CALL_TIMEOUT_MS, undefined, `setRecipients:${key}`
            );
        }
    }

    /**
     * Performs all business logic checks to all the recipients and returns the list of 'To' recipients with hte portals where necessary.
     */
    public async validateSecureEmailAddresses(recipients: string[], unresolvedLists?: string[]): Promise<ValidationResponse> {
        const notificationDialog = new NotificationDialog(this.globalService);

        notificationDialog.showNotificationMessage(NotificationDialog.NotificationFormat.INLINE, NotificationDialog.MessageType.PROGRESS, 'Processing recipients...');

        const apm = this.globalService.apm;
        const validateRecipientsUrl = `${SERVER_URL}/api/3.1/organizations/outlook/check_recipients/`;

        try {
            const recipientsData: Record<string, any> = {
                recipients,
                label_id: this.label?.id || null,
                label_name: this.label?.name || null,
                links: this.nativeLinks,
                trace_id: this.auditTraceId
            };

            if (unresolvedLists && unresolvedLists.length > 0) {
                recipientsData.unresolved_lists = unresolvedLists;
            }

            const response: AxiosResponse<ValidateEmailAddressesResponse> = await axios.post(validateRecipientsUrl, recipientsData, {
                // Axios defaults to 0 (wait forever). This is the core PEP validation call on the
                // send-blocking path, so it must be bounded the same as the rest of AB#14110.
                timeout: HTTP_REQUEST_TIMEOUT_MS,
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': 'JWTBEARER ' + this.cwpToken?.token,
                    'NCC-DEVICE-IDENTIFIER': 'outlook_pep_addin',
                }
            });
            this.nativeLinksToUpdate = response.data.updated_links || [];
            localStorage.setItem("GeApiState", JSON.stringify(this.getState()));

            const current = apm.getCurrentTransaction();

            apm.setCustomContext({ validation_response: JSON.stringify(response.data) });

            // Set elevated label if the response has a label_id and it is different from the current label.
            if (response?.data?.label_id !== null && response?.data?.label_id !== this.label?.id) {
                const labelsCatalog = JSON.parse(localStorage.getItem(`catalog_${this.emailId}`));

                if (!labelsCatalog) {
                    apm.addLabels({ error: 'Labels catalog not found' });
                    throw new Error('Labels catalog not found');
                }

                // Desktop Outlook Mac returns labels catalog with no dashes.
                const responseLabelNormalised = Office.context.platform === Office.PlatformType.Mac
                    ? response.data.label_id.replace(/-/g, "")
                    : response.data.label_id;

                const setLabel = this.globalService.outlookActions.findLabelById(responseLabelNormalised, labelsCatalog);
                if (setLabel) {
                    this.label = setLabel;
                    this.setLabelFromOutlook = true;
                } else {
                    let message = `Priority Label ID (${response.data.label_id}) of attached links was not found in tenants labels catalog, skipping email sensitivity label elevation`;

                    apm.addLabels({ "label-elevation-skipped": true, "reason": message });
                }
            }

            this.extraServerAuditData = response?.data?.extra_data;

            const blockedRecipients = response?.data?.recipients.filter((recipient) => !recipient.is_valid);
            this.allRecipients = this.mergeRecipients(this.originalRecipientsByType, blockedRecipients);

            this.sendAuditEvent(AuditEvents.PDP_DECISION);
            // transaction validation completed.
            current?.end();

            return {
                blockedRecipients,
                is_override_allowed: response.data.is_override_allowed
            };

        } catch (error) {
            if (error?.code === 'ECONNABORTED') {
                apm.getCurrentTransaction()?.addLabels({ "timed-out:check_recipients": true });
                apm.captureError(new Error(`check_recipients timed out after ${HTTP_REQUEST_TIMEOUT_MS}ms calling ${validateRecipientsUrl}`));
            }

            this.globalService.logger.logMessageWithType(LogMessageType.error, `Validate email addresses error: ${error?.message}`);

            throw error;
        }
    }

    public async sendAuditEvent(action: string): Promise<string | void> {
        const apm = this.globalService.apm;
        const auditEventUrl = `${SERVER_URL}/api/3.1/organizations/outlook/auditing/`;

        const storedAuditEvents = localStorage.getItem('auditEvents');
        const auditEvents = storedAuditEvents ? JSON.parse(storedAuditEvents) : [];


        try {
            if (!this.cwpToken || !this.cwpToken.token) {
                return; // No token available, cannot send audit event.
            }

            localStorage.setItem('auditEvents', JSON.stringify([...new Set(auditEvents), action]));
            // send AuditEvents.PDP_DECISION for add-in user force_send action (bypass).
            let forceSendIdentifyAction = action === UserActions.FORCE_SEND ? AuditEvents.PDP_DECISION : action;
            const auditEventData = constructAuditEventData(forceSendIdentifyAction, this.getState());

            const headers = {
                'Content-Type': 'application/json; charset=utf-8',
                'Authorization': 'JWTBEARER ' + this.cwpToken.token,
                'NCC-DEVICE-IDENTIFIER': 'outlook_pep_addin',
            };

            if (action === UserActions.FORCE_SEND) {
                auditEventData.x_header = true;
                auditEventData.outlook.pdp_decision.outcome = auditEventData.outlook?.pdp_decision?.outcome?.map((outcome) => {
                    return {
                        ...outcome,
                        reason: 'bypass'
                    }
                });
                // exclude pdp_service from audit payload on bypass.
                delete auditEventData.outlook.pdp_decision?.pdp_service;

                let response = await axios.post(auditEventUrl, auditEventData, { headers: headers, timeout: HTTP_REQUEST_TIMEOUT_MS });
                return response?.data?.x_header;
            } else {
                if (!AuditEventQueue.isInitialized) {
                    await this.updateServiceWorkerConfig();
                }
                await AuditEventQueue.enqueue(auditEventData);

                return '';
            }

        } catch (error) {
            if (error?.code === 'ECONNABORTED') {
                apm.getCurrentTransaction()?.addLabels({ "timed-out:sendAuditEvent": true });
                apm.captureError(new Error(`sendAuditEvent timed out after ${HTTP_REQUEST_TIMEOUT_MS}ms calling ${auditEventUrl}`));
            }

            this.globalService.logger.logMessageWithType(LogMessageType.error, `Audit event error: ${error?.message}`);
            apm.captureError(error);

            throw error;
        }
    }

    private async handleByPassKeyword(subject: string) {
        this.globalService.logger.logMessageWithType(LogMessageType.info, 'Keyword found');
        let updatedSubject = subject.replace(BYPASS_KEYWORD, '');
        // Both writes are bounded and fail open: the user has asked to bypass, so a wedged host must
        // not block the send. A timeout leaves the keyword visible in the subject, or the bypass
        // unstamped, and is recorded as `timed-out:<operation>`.
        await withTimeoutOr(
            this.globalService.outlookActions.setSubjectToOutlook(updatedSubject),
            OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setSubjectToOutlook:bypass'
        );
        const jwtServerToken = await this.sendAuditEvent(UserActions.FORCE_SEND);

        if(jwtServerToken) await withTimeoutOr(
            this.globalService.outlookActions.setHeaderToOutlook('X-HEADER-TOKEN', jwtServerToken),
            OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setHeaderToOutlook:bypass'
        );
    }

    private async setLabel() {
        const apm = this.globalService.apm;

        try {
            let label = await withTimeout(this.globalService.outlookActions.getSensitivityLabel(), OFFICE_JS_CALL_TIMEOUT_MS);

            if (label) {
                let labelIdFormatted = formatToUuid(label?.id);
                this.label = {...label, id: labelIdFormatted};
            }
        } catch (error) {
            apm.addLabels({ "label-fetch-timeout": true });
            apm.captureError(error);
            this.globalService.logger.logMessageWithType(LogMessageType.error, `Sensitivity label fetch failed or timed out: ${error?.message}`);
        }
    }

    private async setToken() {
        this.outlookToken = await this.globalService.outlookActions.getNAAtoken(Scopes.GRAPH);
        try {
            this.cwpToken = await this.globalService.outlookActions.exchangeTokens(this.outlookToken);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                this.globalService.logger.logMessageWithType(LogMessageType.error, 'User not found, init user onboarding.');
                const dialogUuid = Math.random().toString(36).substring(2, 18) + Math.random().toString(36).substring(2, 18);
                const gePortals = new GePortals(this.globalService);
                const traceId = (this.apmTransaction as Transaction & { traceId: string })?.traceId || "UNKNOWN_TRACE_ID";
                const transactionId = (this.apmTransaction as Transaction & { id: string })?.id || "UNKNOWN_TRANSACTION_ID";
                // store email address in localStorage so it can be used in the onboarding dialog getNAAtoken() invocation.
                localStorage.setItem("emailAddress", Office.context.mailbox.userProfile.emailAddress);

                this.identity = await gePortals.showUserOnBoardingDialog(dialogUuid, this.tenant, traceId, transactionId);

                if (this.identity) {
                    this.cwpToken = await this.globalService.outlookActions.exchangeTokens(this.outlookToken);
                } else {
                    throw new GeException('hook.cancelled', 'User cancelled or not completed onboarding dialog!');
                }
            } else {
                // Continue throwing the error to be handled by the caller.
                throw error;
            }
        }

        this.setDecodedTokenPayload();

        // make token available for error reporting in case of exceptions.
        localStorage.setItem("GeApiState", JSON.stringify(this.getState()));
        localStorage.setItem('cwpToken', JSON.stringify({
            token: this.cwpToken.token,
            expiration: this.cwpToken.expiration,
            emailAddress: this.decodedTokenPayload?.smtp || this.decodedTokenPayload?.upn || this.decodedTokenPayload?.preferred_username || ""
        } as TokenData));

        await this.updateServiceWorkerConfig();
    }

    private async setUpAttachments() {
        const apm = this.globalService.apm;

        try {
            this.attachments = await withTimeout(this.globalService.outlookActions.getEmailAttachments(), OFFICE_JS_CALL_TIMEOUT_MS);
        } catch (error) {
            apm.addLabels({ "attachments-fetch-timeout": true });
            apm.captureError(error);
            this.globalService.logger.logMessageWithType(LogMessageType.error, `Attachments fetch failed or timed out: ${error?.message}`);
            this.attachments = [];
        }
    }

    private async setUpNativeLinks() {
        const apm = this.globalService.apm;

        try {
            this.nativeLinks = await withTimeout(this.globalService.outlookActions.parseEmailBodyForLinks(), OFFICE_JS_CALL_TIMEOUT_MS);
        } catch (error) {
            apm.addLabels({ "links-fetch-timeout": true });
            apm.captureError(error);
            this.globalService.logger.logMessageWithType(LogMessageType.error, `Native links fetch failed or timed out: ${error?.message}`);
            this.nativeLinks = [];
        }
    }

    private logExceptionInformation(exception: GeException | Error) {
        GeException.logException(this.globalService.logger, exception);
    }

    private setDecodedTokenPayload() {
        const jwtToken = this.outlookToken;
        if (jwtToken) {
            this.decodedTokenPayload = jwtDecode(jwtToken);
        } else {
            this.globalService.logger.logMessageWithType(LogMessageType.error, 'Token not found to decode!');
        }
    }

    /**
     * Sends a secure message, runs business logic to filter recipients and associate externals with portals
     * and returns true if the message is allowed to be sent.
     *
     * @returns true if the mail is allowed to be sent, false if it should be blocked
     */
    public async sendSecure(): Promise<GeApi.SendSecureResult> {
        let originalToRecipients: Office.EmailAddressDetails[];
        let subject: string;
        let allOriginalRecipientsByType: RecipientsByAllTypes;
        const apm = this.globalService.apm;

        try {
            this.apmTransaction = apm.getCurrentTransaction();
            const traceId = (this.apmTransaction as Transaction & { traceId: string })?.traceId || "UNKNOWN_TRACE_ID"; //TODO
            const transactionId = (this.apmTransaction as Transaction & { id: string })?.id || "UNKNOWN_TRANSACTION_ID";

            if (this.globalService.outlookActions) {
                this.globalService.outlookActions.traceId = traceId;
                this.globalService.outlookActions.transactionId = transactionId;
            }
    
            this.apmTransaction?.addContext({
                state: JSON.parse(localStorage.getItem("GeApiState")) || this.getState(),
                platform: JSON.parse(localStorage.getItem("platformInfo")),
            });

            if (!Office.context.requirements.isSetSupported("NestedAppAuth", "1.1")) {
                throw new Error("Nested Application Authentication is not supported");
            }

            // get Token fist so error reporting can have it available.
            await this.setToken();

            // Every host call below sits on the send-blocking path of a synchronous ItemSend handler.
            // An Office.js callback that never fires leaves its promise unsettled and wedges the send
            // permanently, so each one is bounded (AB#14110). Each call reports its own APM span, and
            // on timeout the operation name is attached to the transaction as `timed-out:<operation>`.
            await withTimeout(
                this.globalService.outlookActions.setNotificationMessage('error', Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage, null),
                OFFICE_JS_CALL_TIMEOUT_MS,
                'setNotificationMessage'
            );

            // Always set header to outlook add-in outgoing emails.
            await withTimeout(
                this.globalService.outlookActions.setHeaderToOutlook(headerName, headerValue),
                OFFICE_JS_CALL_TIMEOUT_MS,
                'setHeaderToOutlook'
            );

            // It is important to save the draft, so that the notifications can be shown.
            // saveAsync round-trips to Exchange, and through MIP when a protected label is applied, so
            // it gets a larger budget than a local Office.js read.
            //
            // A timeout here means "unknown outcome", not "not saved": Office.js cannot be cancelled,
            // so the save may still land afterwards (withTimeout reports that as a late settlement).
            // In that case emailId stays unset while the draft does exist, which leaks the matching
            // catalog_<emailId> key - index.tsx cleans up catalog_undefined instead. Accepted for now
            // because we fail open and the send proceeds; revisit if late settlements show up in APM.
            this.emailId = await withTimeout(
                this.globalService.outlookActions.saveDraft(),
                SAVE_DRAFT_TIMEOUT_MS,
                'saveDraft'
            );

            const dialogUuid = Math.random().toString(36).substring(2, 18) + Math.random().toString(36).substring(2, 18);
            const gePortals = new GePortals(this.globalService);
            
            const recipientFieldHandler = new EmailFields(this.globalService, this.container, this.itemType);

            // The subject carries BYPASS_KEYWORD, so losing it silently disables a deliberate user
            // action: the bypass is never detected and the mail is validated anyway. These stalls are
            // transient host hiccups rather than permanent failures, so retry once before giving up.
            try {
                subject = await withTimeout(this.globalService.outlookActions.getSubjectFromOutlook(), OFFICE_JS_CALL_TIMEOUT_MS, 'getSubjectFromOutlook');
            } catch (firstAttemptError) {
                apm.addLabels({ "subject-fetch-timeout": true });
                apm.captureError(firstAttemptError);
                this.globalService.logger.logMessageWithType(LogMessageType.error, `Subject fetch failed or timed out, retrying: ${firstAttemptError?.message}`);

                try {
                    subject = await withTimeout(this.globalService.outlookActions.getSubjectFromOutlook(), OFFICE_JS_CALL_TIMEOUT_MS, 'getSubjectFromOutlook:retry');
                } catch (retryError) {
                    // Both attempts failed, so we cannot tell whether the user asked for a bypass.
                    // Falling back to '' resolves that ambiguity as "no bypass", which validates the
                    // mail rather than letting it through - safe, but it silently ignores what the
                    // user asked for, so label it to make the state queryable instead of invisible.
                    apm.addLabels({ "bypass-undeterminable": true });
                    apm.captureError(retryError);
                    this.globalService.logger.logMessageWithType(LogMessageType.error, `Subject fetch failed on retry, bypass cannot be evaluated: ${retryError?.message}`);
                    subject = '';
                }
            }
            this.subject = subject;

            await Promise.all([
                this.setUpAttachments(),
                this.setLabel(),
                this.setUpNativeLinks(),
            ]);

            const hasNoLinksAndNoAttachments = this.nativeLinks.length === 0 && this.attachments.length === 0;

            if ((!this.label?.id || ignoreChecksLabelsId.includes(this.label?.id)) && hasNoLinksAndNoAttachments) {
                // transaction --> email sent no validation
                this.apmTransaction.addLabels({ validation : 'no-validation', reason: 'whitelisted-label-or-no-links-or-attachments' });

                return GeApi.SendSecureResult.SEND_UNSECURE;
            }

            const { recipientsByType, unresolvedLists } = await recipientFieldHandler.resolveAllEmails();
            allOriginalRecipientsByType = recipientsByType;
            this.originalRecipientsByType = JSON.parse(JSON.stringify(allOriginalRecipientsByType));
            this.allRecipients = this.mergeRecipients(allOriginalRecipientsByType, []);
            // Make the class state available to the modal to validate and audit.
            localStorage.setItem("GeApiState", JSON.stringify(this.getState()));
            // Store unresolved lists for the portal dialog to include in validation request.
            if (unresolvedLists.length > 0) {
                localStorage.setItem("unresolvedLists", JSON.stringify(unresolvedLists));
            } else {
                localStorage.removeItem("unresolvedLists");
            }

            if (subject.includes(BYPASS_KEYWORD)) {
                await this.handleByPassKeyword(subject);
                this.apmTransaction.addLabels({ validation : 'no-validation', reason: 'keyword-bypass' });

                return GeApi.SendSecureResult.SEND_UNSECURE;
            }

            // Send right away if Labels requirements are not supported.
            if (!Office.context.requirements.isSetSupported('Mailbox', '1.13')) {
                this.apmTransaction?.addLabels({ validation : 'no-validation', reason: 'labels-not-supported-requirements-set' });

                return GeApi.SendSecureResult.SEND_UNSECURE;
            }

            // Send right away if it is a meeting response.
            // getItemClass resolves '' on failure rather than rejecting, so bound it the same way -
            // adding a timeout must not introduce a rejection path this caller never had to handle.
            const itemClass = await withTimeoutOr(
                this.globalService.outlookActions.getItemClass(),
                OFFICE_JS_CALL_TIMEOUT_MS,
                '',
                'getItemClass'
            );

            if (itemClass.includes("IPM.Schedule.Meeting")) {
                this.apmTransaction?.addLabels({ validation : 'no-validation', reason: 'meeting-response-bypass' });

                return GeApi.SendSecureResult.SEND_UNSECURE;
            }

            // make available to the modal to validate.
            gePortals.saveExternalAddresses(allOriginalRecipientsByType, dialogUuid);
            this.apmTransaction?.addLabels({ validation : 'pep-validation' });
            const validationSpan = this.apmTransaction?.startSpan('PEP validation modal', 'modal', { blocking: true });
            
            (window as any).__currentModalSpan = validationSpan;

            // Show the validation UI modal.
            const { portalItems: originallyBlockedRecipients, headersResponse: syncOverridesResponse } = await gePortals.showValidationDialog(dialogUuid, this.tenant, traceId, transactionId);
            const blockedRecipients = originallyBlockedRecipients.filter((recipient) => !recipient.isValid);
            
            // update class state from dialog.
            this.setState(JSON.parse(localStorage.getItem("GeApiState")));

            this.recipientsOverriddenByUser = originallyBlockedRecipients.filter((recipient) => recipient.isValid && recipient.isJustified);

            // Update message headers if any overrides were made in the modal and synced with server.
            if (syncOverridesResponse) {
                this.apmTransaction?.addLabels({ 'sync-overrides-headers': JSON.stringify(syncOverridesResponse) });
                await withTimeoutOr(
                    this.globalService.outlookActions.setHeadersToOutlook(syncOverridesResponse),
                    OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setHeadersToOutlook:overrides'
                );
            }
            // if validation returned a higher priority or the same label, set it as email label.
            if (this.setLabelFromOutlook) {
                await withTimeoutOr(
                    this.globalService.outlookActions.setSensitivityLabel(this.label.id),
                    OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setSensitivityLabel'
                );
                this.setLabelFromOutlook = false;
            }

            const currentState = JSON.parse(localStorage.getItem("GeApiState"));
            if (currentState.nativeLinksToUpdate?.length > 0) {
                // Update links in the email body only for non-Mac platforms as Mac body.setAsync() causes removed recipients from the thread to be re-added!
                // https://github.com/OfficeDev/office-js/issues/6374
                if (Office.context.platform !== Office.PlatformType.Mac) {
                    // Reads and rewrites the HTML body - two more Office.js round trips, so it gets a
                    // budget of its own rather than the single-call one.
                    await withTimeoutOr(
                        this.globalService.outlookActions.setEmailBodyWithUpdatedLInks(currentState.nativeLinksToUpdate),
                        OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setEmailBodyWithUpdatedLInks'
                    );
                }
            }

            // update the class state with the blocked recipients (again after validation) as they may have
            // been overridden (allowed) in the modal, we need them updated for the case of no blocked recipients as well.
            this.allRecipients = this.mergeRecipients(allOriginalRecipientsByType, originallyBlockedRecipients);

            // go ahead and send if all are allowed
            if (!blockedRecipients?.length) {
                apm.addLabels({ "blocked recipients after validation": "none" });
                return GeApi.SendSecureResult.SEND_UNSECURE;
            }

            apm.addLabels({ "blocked-recipients-after-validation": blockedRecipients.map(rec => rec.email).join(',') });

            // Store blocked recipients to send AuditEvents.USER_DECISION audit event only if the user does not cancel the operation.
            this.recipientsToBeRemoved = blockedRecipients;

            // Filter out blocked recipients from the original recipients ready to set back to the email.
            // check_recipients HTML-escapes emails in its response, so decode before comparing against
            // the plain address on the compose item - otherwise a blocked recipient with an escaped
            // character (e.g. an apostrophe) never matches and is left on the send.
            if (Array.isArray(blockedRecipients)) {
                const blockedEmails = new Set(blockedRecipients.map(rec => decodeHtmlEntities(rec.email)));
                Object.keys(allOriginalRecipientsByType).forEach((recType) => {
                    allOriginalRecipientsByType[recType] = allOriginalRecipientsByType[recType].filter(
                        (recEmail: string) => !blockedEmails.has(recEmail)
                    );
                });
            }
        }
        catch (ex) {
            // Label which bounded call timed out so it is attributable in APM. The exception itself is
            // deliberately left to fall through to `throw ex` below: a timeout must never block the
            // send. The add-in fails open on any unexpected failure (see index.tsx) and a timeout is
            // no different - the mail goes out, and we record why validation could not complete.
            if (ex instanceof TimeoutError) {
                apm.addLabels({ [`timed-out:${ex.operation}`]: true });
            }

            const errorMessage = (() => {
                if (ex instanceof GeException) {
                    const errId = ex.getErrorId();
                    return (errId ? this.globalService.t(errId, ex.getOptions()) : ex.getErrorLog());
                }
                else if (ex instanceof Error) {
                    return ex.message;
                }
                else if (typeof ex === 'string') {
                    return ex;
                }
                else {
                    return JSON.stringify(ex);
                }
            })();
            this.logExceptionInformation(ex);
            // We are already handling a failure, so the host has just proven it may not answer. These
            // notification updates must not be able to wedge the send a second time - bound them and
            // carry on if they do not respond (AB#14110).
            await withTimeoutOr(
                this.globalService.outlookActions.setNotificationMessage('progress', Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage, null),
                NOTIFICATION_TIMEOUT_MS, undefined, 'setNotificationMessage:recovery-progress'
            );
            // expected cases - we don't send - no need for logging
            if ( ["hook.cancelled", "hook.no_recipients", "hook.popup_dialog_closed"].includes(ex?.errorId) ) {
                if (ex?.errorId === "hook.cancelled") {
                    await this.sendAuditEvent(AuditEvents.CANCEL);
                }
                await withTimeoutOr(
                    this.globalService.outlookActions.setNotificationMessage('error', Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage, errorMessage),
                    NOTIFICATION_TIMEOUT_MS, undefined, 'setNotificationMessage:recovery-error'
                );
                apm.addLabels({ exception: errorMessage });

                return GeApi.SendSecureResult.DONT_SEND;
            }

            // something else happened (e.g. 9021 case) let the email go and log
            throw ex;
        }
        try {
            if (subject.length > 255) {
                throw new GeException('hook.subject_too_long', 'Subject line is too long and the addin cannot modify the subject');
            }
            const recipientsArePresent = Object.values(allOriginalRecipientsByType).some(recipients => !!recipients.length);
            if (!recipientsArePresent) return GeApi.SendSecureResult.DONT_SEND;
            await this.setAllRecipients(allOriginalRecipientsByType);
        }
        catch (e) {
            try {
                // Recovery path - restoring the user's original draft after a failure. Bounded so a
                // wedged host cannot turn a handled error into a permanent stall.
                await withTimeoutOr(
                    this.globalService.outlookActions.setRecipients(RecipientType.TO, originalToRecipients),
                    OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setRecipients:recovery'
                );
                await withTimeoutOr(
                    this.globalService.outlookActions.setSubjectToOutlook(subject),
                    OFFICE_JS_CALL_TIMEOUT_MS, undefined, 'setSubjectToOutlook:recovery'
                );
                const notificationDialog = new NotificationDialog(this.globalService);
                notificationDialog.showNotificationMessage(NotificationDialog.NotificationFormat.INLINE, NotificationDialog.MessageType.INFO, null);

                if (e instanceof GeException) {
                    const errId = e.getErrorId();
                    const errorMessage = (errId ? this.globalService.t(errId, e.getOptions()) : e.getErrorLog());

                    // Same reasoning as the outer catch: this runs after a failure, so it must not be
                    // able to block the send a second time (AB#14110).
                    await withTimeoutOr(
                        this.globalService.outlookActions.setNotificationMessage('progress', Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage, null),
                        NOTIFICATION_TIMEOUT_MS, undefined, 'setNotificationMessage:recipients-progress'
                    );
                    await withTimeoutOr(
                        this.globalService.outlookActions.setNotificationMessage('error', Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage, errorMessage),
                        NOTIFICATION_TIMEOUT_MS, undefined, 'setNotificationMessage:recipients-error'
                    );
                }
                this.logExceptionInformation(e);
            } catch(e) {
                throw e;
            }

            throw e;
        }

        return GeApi.SendSecureResult.SEND_UNSECURE;
    }

    private mergeRecipients(recipients: RecipientsByAllTypes, notAllowed: RecipientForDialog[] | ValidateResponseRecipient[]): AuditEmailRecipient[] {
        // Flatten recipients object and merge with not allowed recipients.
        let allRecipients = [];
        for (const [type, emails] of Object.entries(recipients)) {
            emails.forEach((email: string) => {
                const isBlocked = notAllowed.find((blRec: RecipientForDialog) => email === blRec.email);

                allRecipients.push({
                    email,
                    is_cc: type === RecipientType.CC,
                    is_bcc: type === RecipientType.BCC,
                    allowed: !isBlocked,
                    ...(isBlocked && { reason: isBlocked.reason }),
                });
            });
        }

        // Check if notAllowed is of type RecipientForDialog[], meaning it came after validation & justification in the dialog.
        if (notAllowed.length > 0 && 'isValid' in notAllowed[0]) {
            allRecipients = allRecipients.map(recipient => {
                const overriddenRecipient = notAllowed.find(
                    (ovRec: RecipientForDialog) => ovRec.email === recipient.email && ovRec.isValid && ovRec.isJustified
                );
                if (overriddenRecipient) {
                    return {
                        ...recipient,
                        reason: recipient.reason,
                        justification: (overriddenRecipient as RecipientForDialog).justification || '',
                        decision: DECISION_OVERRIDE
                    };
                } else if (!recipient.allowed) {
                    return {
                        ...recipient,
                        allowed: false,
                        reason: recipient.reason || '',
                        decision: DECISION_ACCEPT
                    };
                } else {
                    return recipient;
                }
            });
        }

        return allRecipients;
    }

    private async updateServiceWorkerConfig() {
        const apm = this.globalService.apm;
        
        if (!this.cwpToken?.token) {
            apm.captureError(new Error('No valid token available for service worker config update'));
            if (__DEV__) console.warn('Cannot update service worker config: No valid token available');
            return;
        }
    
        try {
            // Initialize AuditEventQueue only with a valid token
            const hasServiceWorker = await AuditEventQueue.initialize();
            
            if (hasServiceWorker && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'config_update',
                    serverUrl: this.serverUrl,
                    token: this.cwpToken.token,
                    isDev: __DEV__,
                });
                console.log('Service worker config updated with valid token');
                
                AuditEventQueue.checkAndSendPendingEvents();
            }
        } catch (error) {
            apm.captureError(error);
            if (__DEV__) console.error('Failed to update service worker config:', error);
        }
    }
}

export namespace GeApi {
    export enum SendSecureResult {
        SEND_UNSECURE,
        SEND_SECURE,
        DONT_SEND
    }
}